import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Settings, ArrowRight } from "lucide-react";
import { api, fileUrl, getCountry, setCountry as persistCountry, COUNTRY_NAME, HOME_COUNTRIES, COMING_SOON } from "@/lib/api";
import { ToothLogo } from "@/components/ToothLogo";
import SearchSheet from "@/components/SearchSheet";
import SettingsSheet from "@/components/SettingsSheet";
import PlansSheet from "@/components/PlansSheet";
import InstallPrompt from "@/components/InstallPrompt";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function Home() {
  const [config, setConfig] = useState(null);
  const [country, setCountry] = useState(getCountry());
  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [popupCountry, setPopupCountry] = useState(null);

  useEffect(() => {
    api.get(`/config?country=${country}`).then((r) => setConfig(r.data)).catch(() => setConfig({}));
  }, [country]);

  const toggleCountry = () => {
    const next = country === "EC" ? "AR" : "EC";
    setCountry(next);
    persistCountry(next);
  };

  const selectCountry = (c) => {
    setCountry(c);
    persistCountry(c);
  };

  const bg = fileUrl(config?.background_image);
  const brand = config?.brand || "odontopick";

  return (
    <div className="app-shell font-brand text-neutral-900" style={{ height: "100dvh" }}>
      {/* Background */}
      <div className="absolute inset-0">
        {bg && (
          <img src={bg} alt="" className="h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/30" />
      </div>

      {/* Settings */}
      <button
        data-testid="open-settings-btn"
        onClick={() => setSettingsOpen(true)}
        aria-label="Ajustes"
        className="absolute right-5 top-6 z-20 grid h-11 w-11 place-items-center rounded-full text-neutral-700 transition-colors hover:bg-white/40 active:scale-95"
      >
        <Settings strokeWidth={1.8} className="h-6 w-6" />
      </button>

      {/* Brand */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="absolute left-0 right-0 top-[calc(20%+180px)] z-10 flex -translate-y-1/2 items-center justify-center px-10"
      >
        <img
          src="/assets/logo-home.webp"
          alt="odontopick"
          className="w-[143px] max-w-[70%] object-contain"
        />
      </motion.div>

      {/* Bottom capsule */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="glass safe-bottom absolute inset-x-0 bottom-0 top-[calc(100%-300px)] z-10 rounded-t-[45px] px-5 pb-6 pt-7 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]"
      >
        <button
          data-testid="open-search-btn"
          onClick={() => setSearchOpen(true)}
          style={{ width: 320, height: 57, backgroundColor: "rgba(242,244,251,0.69)" }}
          className="mx-auto flex items-center justify-between rounded-full px-7 text-lg font-medium text-neutral-800 shadow-sm ring-1 ring-white/70 transition-transform active:scale-[0.98]"
        >
          <span className="mx-auto whitespace-nowrap" style={{ fontSize: 18, fontWeight: 400 }}>{config?.hero_cta || "Encuentra odontologos cerca"}</span>
          <ArrowRight strokeWidth={1.8} className="h-5 w-5 shrink-0 text-neutral-700" />
        </button>

        <div className="mx-auto mt-6 flex max-w-[340px] flex-wrap items-center justify-center gap-2" data-testid="country-selector">
          {HOME_COUNTRIES.map((c) => {
            const soon = COMING_SOON.includes(c);
            const selected = !soon && country === c;
            return (
              <button
                key={c}
                data-testid={`country-btn-${c}`}
                onClick={() => (soon ? setPopupCountry(c) : selectCountry(c))}
                style={{ fontSize: 12, fontWeight: 400 }}
                className={`rounded-full px-3 py-1 transition-all active:scale-95 ${
                  selected ? "bg-neutral-900 text-white" : "bg-neutral-900/10 text-neutral-900"
                }`}
              >
                {COUNTRY_NAME[c]}
              </button>
            );
          })}
        </div>
        <div className="mt-4 text-center">
          <button
            data-testid="advertise-link"
            onClick={() => setPlansOpen(true)}
            className="text-sm font-normal text-neutral-800 underline-offset-4 hover:underline"
          >
            {config?.advertise_text || "Anúnciate"}
          </button>
        </div>
      </motion.div>

      <Dialog open={!!popupCountry} onOpenChange={(o) => !o && setPopupCountry(null)}>
        <DialogContent className="max-w-[320px] overflow-hidden rounded-3xl border-0 p-0" data-testid="coming-soon-popup">
          {popupCountry && (
            <div className="relative">
              {config?.country_popups?.[popupCountry] ? (
                <img src={fileUrl(config.country_popups[popupCountry])} alt="" className="w-full object-cover" style={{ aspectRatio: "3 / 4" }} />
              ) : (
                <div className="grid w-full place-items-center bg-neutral-100" style={{ aspectRatio: "3 / 4" }}>
                  <span className="text-sm text-neutral-400">Sin imagen</span>
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-5 text-center">
                <p className="font-brand text-2xl font-semibold text-white" data-testid="popup-country-name">{COUNTRY_NAME[popupCountry]}</p>
                <p className="text-sm font-medium text-white/90">Próximamente</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SearchSheet
        open={searchOpen}
        onOpenChange={setSearchOpen}
        country={country}
        onToggleCountry={toggleCountry}
      />
      <SettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} config={config} />
      <PlansSheet open={plansOpen} onOpenChange={setPlansOpen} config={config} />
      <InstallPrompt />
    </div>
  );
}
